import type { ReactNode } from "react"
import { Card } from "@/components/ui/card"
import { Sparkline } from "@/components/ui/sparkline"

// Reusable KPI tile shared across the analytics pages.
//
// Originally extracted from app/javascript/pages/Analytics/Handlers.tsx so
// the SLA Performance page can reuse the same visual treatment. Two
// flavors of API are supported:
//
//   1) Legacy "tile" shape (Handlers.tsx):
//        { value, deltaVsPrior, sparkline }
//      paired with a `formatter` and `fillColor`. Picks an arrow + "vs
//      prior window" caption.
//
//   2) Richer "kpi value" shape (SlaPerformance.tsx) where the backend
//      pre-formats the unit and goal:
//        { value, delta, deltaUnit, goal, sparkline }
//      with optional `goalCmp` ("gte" | "lte") to drive the tone of the
//      card border (red below goal, green above, etc).
//
// Both shapes render the same layout — a label row, a big number, a small
// delta line, and an inline sparkline.

export type KpiTone = "neutral" | "success" | "warning" | "error"

export type KpiValueLike = {
  value: number | null
  delta: number | null
  deltaUnit?: "pp" | "sec" | "abs"
  goal?: number | null
  sparkline?: (number | null)[]
}

export type KpiTileLike = {
  value: number | null
  deltaVsPrior: number | null
  sparkline: (number | null)[]
}

type CommonProps = {
  label: string
  /** Display formatter for the headline value. */
  formatValue?: (n: number | null) => string
  /** Hex color for the area-chart fill. */
  fillColor?: string
  /** Optional override for the tone-driven border. */
  tone?: KpiTone
}

type LegacyProps = CommonProps & {
  tile: KpiTileLike
  formatter?: (n: number | null) => string  // alias of formatValue
  /** Custom delta formatter for the legacy tile shape. */
  formatDelta?: (n: number) => string
  // Discriminator
  kpi?: undefined
}

type RichProps = CommonProps & {
  kpi: KpiValueLike
  /** Direction of "good" — "gte" means higher is better. Drives tone. */
  goalCmp?: "gte" | "lte"
  /** When true, a positive delta is bad (e.g. avg first response). */
  invertDelta?: boolean
  /** Caption rendered below the value, e.g. "vs prior 7 days". */
  priorLabel?: string
  /** Bottom-of-card text. When provided, REPLACES the auto goal copy.
   *  Use for "Idle breaches in range: 43" / "Across all SLA types" /
   *  "No SLA target". Pass a string starting with `🎯 ` to render the
   *  goal-target idiom; otherwise plain muted text. */
  footnote?: string
  /** Render mini-chart as bars (e.g. idle-breach daily counts) rather
   *  than the default smoothed area. */
  sparklineVariant?: "area" | "bar"
  /** When true, append a parenthesized percent change next to the
   *  abs/seconds delta (e.g. "↓ 15 (-25.9%)"). Computed from the
   *  prior-period value when supplied. */
  showDeltaPct?: boolean
  // Discriminator
  tile?: undefined
}

type Props = LegacyProps | RichProps

const TONE_BORDER: Record<KpiTone, string> = {
  neutral: "",
  success: "border-status-success/40",
  warning: "border-status-warning/40",
  error:   "border-status-error/40",
}

export function KpiCard(props: Props) {
  if ("kpi" in props && props.kpi) {
    return <RichKpiCard {...props} />
  }
  return <LegacyKpiCard {...(props as LegacyProps)} />
}

// ─── Rich variant (SLA-style) ───────────────────────────────────────

function RichKpiCard({
  label,
  kpi,
  formatValue,
  fillColor = "#facc15",
  goalCmp = "gte",
  invertDelta = false,
  tone,
  priorLabel = "vs prior 7 days",
  footnote,
  sparklineVariant = "area",
  showDeltaPct = false,
}: RichProps) {
  const fmt = formatValue ?? defaultFormatter(kpi.deltaUnit)
  // `footnote` overrides the auto-computed goal copy. Caller controls
  // exact wording so each card can use its own bottom-line text.
  const bottomText = footnote ?? goalCopyFor(kpi.goal, kpi.deltaUnit, goalCmp, formatValue)
  const resolvedTone = tone ?? toneFor(kpi.value, kpi.goal, goalCmp)
  const sparkline = kpi.sparkline ?? []
  const deltaPct = showDeltaPct ? deltaPercent(kpi.value, kpi.delta) : null

  return (
    <Card className={`p-4 ${TONE_BORDER[resolvedTone]}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-medium uppercase tracking-wide text-app-muted">{label}</div>
      </div>
      <div className="mt-2 grid gap-2 grid-cols-[1fr_auto] items-center">
        <div>
          <div className="flex items-baseline gap-2">
            <div className="text-2xl font-semibold tracking-tight text-app-text">{fmt(kpi.value)}</div>
            <span className="text-xs">
              {renderDelta(kpi.delta, kpi.deltaUnit ?? "abs", { invert: invertDelta, deltaPct })}
            </span>
          </div>
          <div className="text-[11px] text-app-subtle mt-0.5">{priorLabel}</div>
        </div>
        <div className="w-[100px] h-12">
          {sparkline.length > 1 ? (
            <Sparkline values={sparkline} height={48} variant={sparklineVariant} fillColor={fillColor} />
          ) : (
            <div className="h-full rounded-control bg-white/[0.03]" />
          )}
        </div>
      </div>
      {bottomText && <div className="mt-3 text-[11px] text-app-muted">{bottomText}</div>}
    </Card>
  )
}

// ─── Legacy variant (Handlers-style) ────────────────────────────────

function LegacyKpiCard({
  label,
  tile,
  formatter,
  formatValue,
  formatDelta,
  fillColor = "#ef4444",
  tone = "neutral",
}: LegacyProps) {
  const fmt = formatValue ?? formatter ?? ((n: number | null) => (n == null ? "—" : String(n)))
  return (
    <Card className={`p-4 ${TONE_BORDER[tone]}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-app-muted">{label}</div>
      <div className="mt-1 grid gap-2 lg:grid-cols-[1fr_minmax(110px,1fr)]">
        <div>
          <div className="text-3xl font-semibold tracking-tight">{fmt(tile.value)}</div>
          <div className="text-xs text-app-subtle">
            {tile.deltaVsPrior == null
              ? "—"
              : formatDelta
                ? formatDelta(tile.deltaVsPrior)
                : defaultLegacyDelta(tile.deltaVsPrior)}
            <span className="ml-1">vs prior window</span>
          </div>
        </div>
        <div className="h-14">
          <Sparkline values={tile.sparkline} height={56} variant="area" fillColor={fillColor} />
        </div>
      </div>
    </Card>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────

function defaultFormatter(unit?: "pp" | "sec" | "abs"): (n: number | null) => string {
  if (unit === "pp") return formatPctValue
  if (unit === "sec") return formatSecondsValue
  return (n) => (n == null ? "—" : n.toLocaleString())
}

function formatPctValue(n: number | null): string {
  if (n == null) return "—"
  return `${n.toFixed(1)}%`
}

function formatSecondsValue(n: number | null): string {
  if (n == null) return "—"
  if (n < 60) return `${Math.round(n)}s`
  if (n < 3600) return `${Math.round(n / 60)}m`
  return `${(n / 3600).toFixed(1)}h`
}

function renderDelta(
  delta: number | null,
  unit: "pp" | "sec" | "abs",
  opts: { invert?: boolean; deltaPct?: number | null } = {},
): ReactNode {
  if (delta == null) return <span className="text-app-muted">—</span>
  const better = opts.invert ? delta < 0 : delta > 0
  const worse  = opts.invert ? delta > 0 : delta < 0
  const flat   = delta === 0
  const cls = flat
    ? "text-app-muted"
    : better
      ? "text-status-success"
      : worse
        ? "text-status-error"
        : "text-app-muted"
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "→"
  const abs = Math.abs(delta)
  let label: string
  if (unit === "pp") label = `${abs.toFixed(1)}pp`
  else if (unit === "sec") label = formatSecondsValue(abs)
  else label = abs.toLocaleString()
  const pctSuffix = opts.deltaPct != null && Number.isFinite(opts.deltaPct)
    ? ` (${opts.deltaPct > 0 ? "+" : ""}${opts.deltaPct.toFixed(1)}%)`
    : ""
  return <span className={cls}>{arrow} {label}{pctSuffix}</span>
}

function deltaPercent(value: number | null, delta: number | null): number | null {
  if (value == null || delta == null) return null
  const prior = value - delta
  if (prior === 0) return null
  return (delta / Math.abs(prior)) * 100
}

function goalCopyFor(
  goal: number | null | undefined,
  unit: "pp" | "sec" | "abs" | undefined,
  cmp: "gte" | "lte",
  formatValue?: (n: number | null) => string,
): string | null {
  if (goal == null) return null
  const fmt = formatValue ?? defaultFormatter(unit)
  const arrow = cmp === "gte" ? "≥" : "≤"
  return `Target ${arrow} ${fmt(goal)}`
}

function toneFor(
  value: number | null,
  goal: number | null | undefined,
  cmp: "gte" | "lte",
): KpiTone {
  if (value == null || goal == null) return "neutral"
  if (cmp === "gte") {
    if (value >= goal) return "success"
    if (value >= goal * 0.95) return "warning"
    return "error"
  }
  // "lte" — lower is better
  if (value <= goal) return "success"
  if (value <= goal * 1.05) return "warning"
  return "error"
}

function defaultLegacyDelta(n: number): string {
  const s = n >= 0 ? "↑" : "↓"
  return `${s} ${(Math.abs(n) * 100).toFixed(1)}pp`
}
